type ProductImageAltSource = {
  name?: unknown;
  title?: unknown;
  manufacturer?: {
    name?: unknown;
  } | null;
};

type ProductGalleryImage = Record<string, any>;

const cleanText = (value: unknown): string =>
  String(value ?? "")
    .replace(/\s+/g, " ")
    .trim();

const comparisonKey = (value: unknown): string =>
  cleanText(value)
    .toLocaleLowerCase("en-US")
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();

const containsWholePhrase = (text: string, phrase: string): boolean =>
  Boolean(phrase) && ` ${text} `.includes(` ${phrase} `);

/**
 * One canonical label for product imagery. A manufacturer is prepended only
 * when the product name does not already contain it as a complete phrase.
 */
export function buildBrandedProductName(
  product: ProductImageAltSource | null | undefined
): string {
  const name = cleanText(product?.name) || cleanText(product?.title);
  const brand = cleanText(product?.manufacturer?.name);
  const nameKey = comparisonKey(name);
  const brandKey = comparisonKey(brand);

  if (!brand) return name || "Designer eyewear";
  if (!name) return brand;
  if (containsWholePhrase(nameKey, brandKey)) return name;
  return `${brand} ${name}`;
}

/** Product-card alt text: brand + product name, without artificial numbering. */
export function buildProductCardImageAlt(
  product: ProductImageAltSource | null | undefined
): string {
  return buildBrandedProductName(product);
}

/**
 * PDP gallery alt text. `photoNumber` is the position among photos, not media
 * slides, so inserting a video never changes image numbering.
 */
export function buildProductGalleryImageAlt(
  product: ProductImageAltSource | null | undefined,
  photoNumber: unknown
): string {
  const number = Math.max(1, Math.floor(Number(photoNumber) || 1));
  return `${buildBrandedProductName(product)} product photo ${number}`;
}

export function buildProductVideoAlt(
  product: ProductImageAltSource | null | undefined
): string {
  return `${buildBrandedProductName(product)} product video`;
}

/**
 * Normalize the PDP photo list once: primary image first, duplicate URLs
 * removed, then deterministic 1-based photo alt text applied.
 */
export function buildProductGalleryImages(
  product: (ProductImageAltSource & Record<string, any>) | null | undefined
): ProductGalleryImage[] {
  if (!product) return [];

  const galleryImages = Array.isArray(product.images) ? product.images : [];
  const candidates: ProductGalleryImage[] = [];

  if (product.image) {
    candidates.push({
      image: product.image,
      imageLazy: product.imageLazy,
      imageBig: product.imageBig || product.image,
    });
  }
  candidates.push(...galleryImages);

  const seen = new Set<string>();
  return candidates
    .filter((item) => {
      const url = cleanText(item?.image || item?.src || item?.imageBig);
      if (!url || seen.has(url)) return false;
      seen.add(url);
      return true;
    })
    .map((item, index) => ({
      ...item,
      alt: buildProductGalleryImageAlt(product, index + 1),
    }));
}

/**
 * Keep the existing presentation order (hero photo, videos, remaining
 * photos), without renumbering the already-normalized photos.
 */
export function mergeProductMediaItems(
  photos: ProductGalleryImage[],
  videos: ProductGalleryImage[]
): ProductGalleryImage[] {
  if (!videos.length) return photos;
  if (!photos.length) return videos;
  return [photos[0], ...videos, ...photos.slice(1)];
}
